import type { TonoEstado } from "../../components/ui/patrones";
import { cn } from "../../lib/cn";
import type { EstadoImpresora } from "../../lib/consola-api";

/**
 * Los cuatro estados de impresora con su presentación.
 *
 * **Copiado literal de `types/printer.ts` de `imprenta`** (ADR 0027): las
 * etiquetas en castellano, los colores y el punto que late cuando está en uso.
 * Vive aparte de la página porque la edición inline del tablero lo usa también.
 */

export const ESTADOS_IMPRESORA: Record<
  EstadoImpresora,
  { etiqueta: string; texto: string; fondo: string; punto: string }
> = {
  IDLE: {
    etiqueta: "Disponible",
    texto: "text-green-700",
    fondo: "bg-green-50",
    punto: "bg-green-500",
  },
  BUSY: {
    etiqueta: "En Uso",
    texto: "text-amber-700",
    fondo: "bg-amber-50",
    punto: "bg-amber-500",
  },
  OFFLINE: {
    etiqueta: "Offline",
    texto: "text-neutral-500",
    fondo: "bg-neutral-100",
    punto: "bg-neutral-400",
  },
  MAINTENANCE: {
    etiqueta: "Mantenimiento",
    texto: "text-red-700",
    fondo: "bg-red-50",
    punto: "bg-red-500",
  },
};

export const ORDEN_ESTADOS = ["IDLE", "BUSY", "OFFLINE", "MAINTENANCE"] as const;

/**
 * El mismo color, para la familia de tarjetas de estado (ADR 0094).
 *
 * Vive acá y no en la página para que no se separe de `ESTADOS_IMPRESORA`: si
 * el badge dice verde y la tarjeta dice ámbar, la franja de arriba y la lista de
 * abajo dejan de ser la misma información.
 */
export const TONO_DE_ESTADO: Record<EstadoImpresora, TonoEstado> = {
  IDLE: "verde",
  BUSY: "ambar",
  OFFLINE: "gris",
  MAINTENANCE: "rojo",
};

/**
 * El rótulo de la TARJETA, que no siempre es el del badge.
 *
 * La tarjeta cuenta impresoras —«3 Disponibles»— y el badge califica a una
 * sola —«Disponible»—. `imprenta` los distingue y acá salía el del badge, que
 * al lado de un número se lee mal.
 */
export const ETIQUETA_TARJETA: Record<EstadoImpresora, string> = {
  IDLE: "Disponibles",
  BUSY: "En Uso",
  OFFLINE: "Offline",
  MAINTENANCE: "Mantenimiento",
};

export function EstadoImpresoraBadge({
  estado,
  tamano = "md",
}: {
  estado: EstadoImpresora;
  tamano?: "sm" | "md";
}) {
  const config = ESTADOS_IMPRESORA[estado];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-semibold",
        config.fondo,
        config.texto,
        tamano === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "rounded-full",
          config.punto,
          tamano === "sm" ? "h-1.5 w-1.5" : "h-2 w-2",
          // Sólo "En Uso" late: es el único estado que describe algo que está
          // pasando ahora. `imprenta` hace lo mismo.
          estado === "BUSY" && "animate-pulse",
        )}
      />
      {config.etiqueta}
    </span>
  );
}
